import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, RefreshCw, Star, Upload, X, Image as ImageIcon, UserCircle2 as UserCircle2Icon } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="section-pad text-center text-silver-400">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace/>;

  return (
    <main className="section-pad" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ice-400">Admin</div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black">Dashboard</h1>
            <p className="text-silver-400 text-sm mt-1">Signed in as {user.email}</p>
          </div>
          <button onClick={logout} className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2" data-testid="admin-logout"><LogOut className="size-4"/> Sign out</button>
        </div>

        <Tabs defaultValue="schedules" className="mt-8">
          <TabsList className="bg-ink-800 ring-1 ring-white/10 p-1 rounded-full flex-wrap">
            {["schedules","estimates","contacts","reviews","projects","specials","team","pricing","settings"].map(t => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="rounded-full data-[state=active]:bg-ice-400 data-[state=active]:text-ink-900 px-4 capitalize">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="schedules"><InboxList endpoint="/admin/schedules" label="Appointment Requests" testid="inbox-schedules" fields={["name","phone","city","service_type","preferred_date","preferred_time","notes"]}/></TabsContent>
          <TabsContent value="estimates"><InboxList endpoint="/admin/estimates" label="Estimate Requests" testid="inbox-estimates" fields={["name","phone","city","home_size","system_type","job_type","estimated_low","estimated_high"]}/></TabsContent>
          <TabsContent value="contacts"><InboxList endpoint="/admin/contacts" label="Contact Messages" testid="inbox-contacts" fields={["name","phone","email","kind","message","message_en","language"]}/></TabsContent>

          <TabsContent value="reviews"><ReviewsAdmin/></TabsContent>
          <TabsContent value="projects"><ProjectsAdmin/></TabsContent>
          <TabsContent value="specials"><SpecialsAdmin/></TabsContent>
          <TabsContent value="team"><TeamAdmin/></TabsContent>
          <TabsContent value="pricing"><PricingAdmin/></TabsContent>
          <TabsContent value="settings"><SettingsAdmin/></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function InboxList({ endpoint, label, fields, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(endpoint); setItems(r.data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="mt-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-bold">{label}</h2><button onClick={load} className="btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5"><RefreshCw className={`size-3.5 ${loading?"animate-spin":""}`}/> Refresh</button></div>
      {items.length===0 ? <div className="text-silver-400 text-sm">No items yet.</div> : (
        <div className="grid gap-3">
          {items.map((it,i) => (
            <div key={it.id||i} className="card-ink rounded-2xl p-5" data-testid={`${testid}-item-${i}`}>
              <div className="text-xs text-silver-400">{it.created_at}</div>
              <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {fields.map(f => it[f] != null && (
                  <div key={f}><span className="text-silver-500 uppercase tracking-wider text-[10px] mr-2">{f}</span><span>{String(it[f])}</span></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CrudList({ endpoint, label, blank, fields, testid }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const load = async () => { const r = await api.get(endpoint.replace("/admin","")); setItems(r.data || []); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post(endpoint, form); toast.success("Added"); setForm(blank); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`${endpoint}/${id}`); load(); };
  return (
    <div className="mt-6" data-testid={testid}>
      <h2 className="font-display text-xl font-bold mb-4">{label}</h2>
      <div className="card-ink rounded-2xl p-5 grid gap-3">
        {fields.map(f => (
          <input key={f.key} placeholder={f.label} type={f.type||"text"} className="input" value={form[f.key]??""} onChange={e=>setForm(s=>({...s,[f.key]: f.type==="number"? Number(e.target.value): e.target.value}))} data-testid={`${testid}-input-${f.key}`}/>
        ))}
        <button onClick={add} className="btn-primary rounded-full px-4 py-2 text-sm flex items-center gap-1.5 w-fit" data-testid={`${testid}-add`}><Plus className="size-4"/> Add</button>
      </div>
      <div className="mt-5 grid gap-3">
        {items.map((it,i) => (
          <div key={it.id||i} className="card-ink rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`${testid}-row-${i}`}>
            <div className="text-sm">{fields.map(f => it[f.key] && <span key={f.key} className="mr-3"><b>{f.label}:</b> {String(it[f.key]).slice(0,80)}</span>)}</div>
            <button onClick={()=>del(it.id)} className="text-red-400 hover:text-red-300" data-testid={`${testid}-del-${i}`}><Trash2 className="size-4"/></button>
          </div>
        ))}
      </div>
      <style>{`.input{background:#1C274C;border-radius:.75rem;padding:.65rem .9rem;font-size:.875rem;outline:none;width:100%;border:1px solid rgba(255,255,255,0.08);color:white;}`}</style>
    </div>
  );
}

const ReviewsAdmin = () => <ReviewsCrud/>;

function ReviewsCrud() {
  const [items, setItems] = useState([]);
  const blank = { name: "", city: "", rating: 5, text: "", photo: null, date: "", video_url: "" };
  const [form, setForm] = useState(blank);
  const load = async () => { const r = await api.get("/reviews"); setItems(r.data || []); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onPhoto = async (e) => { const f = e.target.files?.[0]; if (!f) return; set("photo", await fileToB64(f)); };
  const add = async () => {
    if (!form.name || !form.rating) { toast.error("Name and rating required"); return; }
    try { await api.post("/admin/reviews", form); toast.success("Review added"); setForm(blank); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  return (
    <div className="mt-6" data-testid="crud-reviews">
      <h2 className="font-display text-xl font-bold mb-4">Customer Reviews</h2>
      <div className="card-ink rounded-2xl p-5 grid gap-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Customer Name" className="input" value={form.name} onChange={e=>set("name",e.target.value)} data-testid="rev-name"/>
          <input placeholder="City" className="input" value={form.city} onChange={e=>set("city",e.target.value)} data-testid="rev-city"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <select className="input" value={form.rating} onChange={e=>set("rating", Number(e.target.value))} data-testid="rev-rating">
            {[5,4,3,2,1].map(r=> <option key={r} value={r}>{r} Stars</option>)}
          </select>
          <input type="date" className="input" value={form.date} onChange={e=>set("date",e.target.value)} data-testid="rev-date"/>
        </div>
        <textarea placeholder="Review text" className="input min-h-[100px]" value={form.text} onChange={e=>set("text",e.target.value)} data-testid="rev-text"/>
        <input placeholder="Video Testimonial URL (optional)" className="input" value={form.video_url} onChange={e=>set("video_url",e.target.value)} data-testid="rev-video"/>
        <div className="flex items-center gap-3">
          <label className="btn-ghost rounded-full px-4 py-2 text-xs cursor-pointer flex items-center gap-2"><Upload className="size-3.5"/> Photo
            <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="rev-photo"/>
          </label>
          {form.photo && <img src={form.photo} alt="" className="size-10 rounded-full object-cover ring-1 ring-white/10"/>}
          <button onClick={add} className="btn-primary rounded-full px-4 py-2 text-sm ml-auto flex items-center gap-1.5" data-testid="rev-add"><Plus className="size-4"/> Add Review</button>
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        {items.map((it, i) => (
          <div key={it.id||i} className="card-ink rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`rev-row-${i}`}>
            <div className="flex items-center gap-3 text-sm">
              {it.photo ? <img src={it.photo} alt="" className="size-10 rounded-full object-cover"/> : <Star className="size-5 text-ice-400"/>}
              <div>
                <div className="font-semibold">{it.name} <span className="text-silver-500">· {it.rating}★</span></div>
                <div className="text-xs text-silver-400">{it.city}{it.date ? ` · ${it.date}` : ""}</div>
                <div className="text-xs text-silver-500 mt-1 line-clamp-1 max-w-md">{it.text}</div>
              </div>
            </div>
            <button onClick={()=>del(it.id)} className="text-red-400 hover:text-red-300" data-testid={`rev-del-${i}`}><Trash2 className="size-4"/></button>
          </div>
        ))}
      </div>
      <style>{`.input{background:#1C274C;border-radius:.75rem;padding:.65rem .9rem;font-size:.875rem;outline:none;width:100%;border:1px solid rgba(255,255,255,0.08);color:white;}`}</style>
    </div>
  );
}

const ProjectsAdmin = () => <CrudList endpoint="/admin/projects" label="Projects" testid="crud-projects"
  blank={{title:"",city:"",equipment:"",description:"",date_completed:""}}
  fields={[{key:"title",label:"Title"},{key:"city",label:"City"},{key:"equipment",label:"Equipment"},{key:"description",label:"Description"},{key:"date_completed",label:"Date"}]}/>;

const SpecialsAdmin = () => <CrudList endpoint="/admin/specials" label="Special Offers" testid="crud-specials"
  blank={{title:"",description:"",discount:"",expires:"",active:true}}
  fields={[{key:"title",label:"Title"},{key:"description",label:"Description"},{key:"discount",label:"Discount"},{key:"expires",label:"Expires"}]}/>;

const TeamAdmin = () => <TeamCrud/>;

function TeamCrud() {
  const [items, setItems] = useState([]);
  const blank = { name: "", role: "", bio: "", photo: null };
  const [form, setForm] = useState(blank);
  const load = async () => { const r = await api.get("/team"); setItems(r.data || []); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onPhoto = async (e) => { const f = e.target.files?.[0]; if (!f) return; set("photo", await fileToB64(f)); };
  const add = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try { await api.post("/admin/team", form); toast.success("Team member added"); setForm(blank); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/team/${id}`); load(); };
  return (
    <div className="mt-6" data-testid="crud-team">
      <h2 className="font-display text-xl font-bold mb-4">Team Members</h2>
      <div className="card-ink rounded-2xl p-5 grid gap-3">
        <input placeholder="Name (e.g. Jose Velasco)" className="input" value={form.name} onChange={e=>set("name",e.target.value)} data-testid="team-name"/>
        <input placeholder="Role (e.g. Owner, Lead Technician)" className="input" value={form.role} onChange={e=>set("role",e.target.value)} data-testid="team-role"/>
        <textarea placeholder="Short bio (optional)" className="input min-h-[80px]" value={form.bio} onChange={e=>set("bio",e.target.value)} data-testid="team-bio"/>
        <div className="flex items-center gap-3">
          <label className="btn-ghost rounded-full px-4 py-2 text-xs cursor-pointer flex items-center gap-2"><Upload className="size-3.5"/> Photo
            <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="team-photo"/>
          </label>
          {form.photo && <img src={form.photo} alt="" className="size-10 rounded-full object-cover ring-1 ring-white/10"/>}
          <button onClick={add} className="btn-primary rounded-full px-4 py-2 text-sm ml-auto flex items-center gap-1.5" data-testid="team-add"><Plus className="size-4"/> Add Member</button>
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        {items.map((it, i) => (
          <div key={it.id||i} className="card-ink rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`team-row-${i}`}>
            <div className="flex items-center gap-3">
              {it.photo ? <img src={it.photo} alt="" className="size-10 rounded-full object-cover"/> : <UserCircle2Icon className="size-10 text-silver-500"/>}
              <div>
                <div className="font-semibold text-sm">{it.name}</div>
                <div className="text-xs text-silver-400">{it.role}</div>
              </div>
            </div>
            <button onClick={()=>del(it.id)} className="text-red-400 hover:text-red-300" data-testid={`team-del-${i}`}><Trash2 className="size-4"/></button>
          </div>
        ))}
      </div>
      <style>{`.input{background:#1C274C;border-radius:.75rem;padding:.65rem .9rem;font-size:.875rem;outline:none;width:100%;border:1px solid rgba(255,255,255,0.08);color:white;}`}</style>
    </div>
  );
}

function PricingAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({brand:"",tonnage:"",price:0});
  const load = async () => { const r = await api.get("/pricing"); setItems(r.data); };
  useEffect(()=>{ load(); }, []);
  const save = async () => { try { await api.post("/admin/pricing", { ...form, price: Number(form.price) }); toast.success("Saved"); setForm({brand:"",tonnage:"",price:0}); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="mt-6" data-testid="crud-pricing">
      <h2 className="font-display text-xl font-bold mb-4">Pricing</h2>
      <div className="card-ink rounded-2xl p-5 grid sm:grid-cols-4 gap-3">
        <input placeholder="Brand" className="input" value={form.brand} onChange={e=>setForm(f=>({...f,brand:e.target.value}))} data-testid="pricing-brand"/>
        <input placeholder="Tonnage (e.g. 3 Ton)" className="input" value={form.tonnage} onChange={e=>setForm(f=>({...f,tonnage:e.target.value}))} data-testid="pricing-tonnage"/>
        <input type="number" placeholder="Price" className="input" value={form.price} onChange={e=>setForm(f=>({...f,price:e.target.value}))} data-testid="pricing-price"/>
        <button onClick={save} className="btn-primary rounded-full px-4 py-2 text-sm" data-testid="pricing-save">Save</button>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((p,i) => (
          <div key={p.id||i} className="card-ink rounded-2xl p-4 text-sm" data-testid={`pricing-row-${i}`}>
            <div className="font-bold">{p.brand}</div><div>{p.tonnage}</div><div className="text-ice-400 font-semibold">${Number(p.price).toLocaleString()}</div>
          </div>
        ))}
      </div>
      <style>{`.input{background:#1C274C;border-radius:.75rem;padding:.65rem .9rem;font-size:.875rem;outline:none;width:100%;border:1px solid rgba(255,255,255,0.08);color:white;}`}</style>
    </div>
  );
}


function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
}

function SettingsAdmin() {
  const { settings, refresh } = useSettings();
  const [logo, setLogo] = useState(null);
  const [heroImage, setHeroImage] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogo(settings.logo || null);
    setHeroImage(settings.hero_image || null);
    setTrucks(settings.trucks || []);
  }, [settings]);

  const onLogo = async (e) => { const f = e.target.files?.[0]; if (!f) return; setLogo(await fileToB64(f)); };
  const onHero = async (e) => { const f = e.target.files?.[0]; if (!f) return; setHeroImage(await fileToB64(f)); };
  const onTrucks = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const b64 = await Promise.all(files.map(fileToB64));
    setTrucks(prev => [...prev, ...b64].slice(0, 6));
  };
  const removeTruck = (i) => setTrucks(t => t.filter((_,k)=>k!==i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { logo, hero_image: heroImage, trucks });
      toast.success("Settings saved");
      refresh();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 grid gap-6" data-testid="crud-settings">
      <h2 className="font-display text-xl font-bold">Site Settings</h2>

      <div className="card-ink rounded-2xl p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-silver-400">Logo</div>
        <div className="mt-3 flex items-center gap-4">
          <div className="size-20 rounded-2xl bg-ink-700 ring-1 ring-white/10 grid place-items-center overflow-hidden">
            {logo ? <img src={logo} alt="logo" className="max-h-full max-w-full object-contain"/> : <ImageIcon className="size-6 text-silver-500"/>}
          </div>
          <label className="btn-ghost rounded-full px-4 py-2 text-xs cursor-pointer flex items-center gap-2"><Upload className="size-3.5"/> Upload Logo<input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="settings-logo-input"/></label>
          {logo && <button onClick={()=>setLogo(null)} className="text-xs text-red-400">Remove</button>}
        </div>
      </div>

      <div className="card-ink rounded-2xl p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-silver-400">Hero Background Image</div>
        <div className="mt-3 flex items-center gap-4">
          <div className="w-40 aspect-[16/9] rounded-2xl bg-ink-700 ring-1 ring-white/10 overflow-hidden grid place-items-center">
            {heroImage ? <img src={heroImage} alt="hero" className="w-full h-full object-cover"/> : <ImageIcon className="size-6 text-silver-500"/>}
          </div>
          <label className="btn-ghost rounded-full px-4 py-2 text-xs cursor-pointer flex items-center gap-2"><Upload className="size-3.5"/> Upload Hero<input type="file" accept="image/*" onChange={onHero} className="hidden" data-testid="settings-hero-input"/></label>
          {heroImage && <button onClick={()=>setHeroImage(null)} className="text-xs text-red-400">Reset</button>}
        </div>
        <p className="mt-2 text-xs text-silver-500">Wide landscape image works best (1800×900+).</p>
      </div>

      <div className="card-ink rounded-2xl p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-silver-400">Truck / Vehicle Photos (up to 6)</div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {trucks.map((src, i) => (
            <div key={src.slice(-32) + i} className="relative" data-testid={`truck-thumb-${i}`}>
              <img src={src} alt="" className="w-full aspect-square object-cover rounded-xl ring-1 ring-white/10"/>
              <button onClick={()=>removeTruck(i)} className="absolute -top-1.5 -right-1.5 size-5 rounded-full bg-red-500 grid place-items-center"><X className="size-3"/></button>
            </div>
          ))}
        </div>
        <label className="mt-3 btn-ghost rounded-full px-4 py-2 text-xs cursor-pointer inline-flex items-center gap-2"><Upload className="size-3.5"/> Add Truck Photos<input type="file" multiple accept="image/*" onChange={onTrucks} className="hidden" data-testid="settings-trucks-input"/></label>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary rounded-full px-6 py-3 text-sm disabled:opacity-50" data-testid="settings-save">{saving ? "Saving..." : "Save Settings"}</button>
      </div>
    </div>
  );
}
